import { motion } from "framer-motion";
import { Reveal, Overline } from "@/components/Reveal";
import { PetitionCTA } from "@/components/PetitionCTA";

export const NoiseExplainer = () => (
  <section id="geluid" data-testid="noise-section" className="scroll-mt-24 bg-gooi-green py-24 text-gooi-offwhite md:py-32">
    <div className="mx-auto max-w-[90rem] px-5 md:px-12">
      <div className="grid gap-14 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-6">
          <Reveal>
            <Overline light>Geluid uitgelegd</Overline>
            <h2 className="mt-4 font-display text-3xl font-medium leading-tight tracking-tight md:text-5xl">
              2 dB klinkt misschien klein. Maar decibellen werken niet lineair.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-gooi-offwhite/75 md:text-lg">
              Een reductie van 2 dB komt overeen met circa 37% minder geluidsenergie. Dat is niet hetzelfde als
              “37% minder herrie”: geluidsenergie, het gemeten geluidsniveau in decibel en subjectief ervaren
              luidheid zijn drie verschillende dingen.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <dl className="mt-10 space-y-5 border-t border-gooi-offwhite/15 pt-8 text-sm md:text-base">
              <div className="grid gap-1 sm:grid-cols-[11rem_1fr] sm:gap-6">
                <dt className="font-semibold text-gooi-offwhite">Geluidsenergie</dt>
                <dd className="text-gooi-offwhite/70">
                  De fysieke hoeveelheid geluid die het verkeer uitstuurt. Daar gaat de 37% over.
                </dd>
              </div>
              <div className="grid gap-1 sm:grid-cols-[11rem_1fr] sm:gap-6">
                <dt className="font-semibold text-gooi-offwhite">Niveau in dB</dt>
                <dd className="text-gooi-offwhite/70">
                  De logaritmische schaal waarin geluid wordt gemeten. Kleine stappen, reëel effect.
                </dd>
              </div>
              <div className="grid gap-1 sm:grid-cols-[11rem_1fr] sm:gap-6">
                <dt className="font-semibold text-gooi-offwhite">Ervaren luidheid</dt>
                <dd className="text-gooi-offwhite/70">
                  Hoe luid iets voor jou klinkt. Die ervaring verschilt per persoon en per moment.
                </dd>
              </div>
            </dl>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-gooi-offwhite/15 bg-gooi-anthracite/40 p-8 md:p-12">
              <p className="text-xs uppercase tracking-[0.25em] text-gooi-offwhite/60">
                Geluidsenergie bij −2 dB
              </p>
              <div className="mt-8 space-y-8">
                <div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-semibold text-gooi-offwhite/80">100 km/u</span>
                    <span className="font-display text-2xl font-semibold tracking-tight">100%</span>
                  </div>
                  <div className="mt-3 h-5 overflow-hidden rounded-full bg-gooi-offwhite/10">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.4, ease: [0.33, 1, 0.68, 1], delay: 0.2 }}
                      className="h-full rounded-full bg-gooi-offwhite/50"
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-semibold text-gooi-offwhite">80 km/u</span>
                    <span className="font-display text-2xl font-semibold tracking-tight text-gooi-red">63%</span>
                  </div>
                  <div className="mt-3 h-5 overflow-hidden rounded-full bg-gooi-offwhite/10">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "63%" }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.4, ease: [0.33, 1, 0.68, 1], delay: 0.5 }}
                      className="h-full rounded-full bg-gooi-red"
                    />
                  </div>
                </div>
              </div>
              <p
                data-testid="noise-energy-claim"
                className="mt-10 font-display text-2xl font-medium tracking-tight md:text-3xl"
              >
                ≈ 37% minder geluidsenergie
              </p>
              <p className="mt-3 text-sm leading-relaxed text-gooi-offwhite/60">
                bij een reductie van 2 dB. Het verschil in ervaren luidheid is niet gelijk aan 37%.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-xs leading-relaxed text-gooi-offwhite/50">
              Het daadwerkelijke geluideffect op het A1-traject is afhankelijk van onder andere
              verkeerssamenstelling, werkelijk gereden snelheid, wegdek, afstand en lokale omstandigheden.
            </p>
            <div className="mt-8">
              <PetitionCTA location="noise" tone="light" />
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
